import { useLocalSearchParams } from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { useResendMagicLink } from 'features/otp/hooks/useResendMagicLink/useResendMagicLink';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import EmailVerificationScreen from 'shared/components/EmailVerificationScreen/EmailVerificationScreen';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';

import VerifyIdentityErrorScreen from './VerifyIdentityError/VerifyIdentityError';

type VerifyIdentitySearchParams = {
  challengeId: string;
  identityId: string;
  emailAddress: string;
};

const VerifyIdentity = () => {
  const { t: translatedTextOtp } = useTranslation('otp');
  const { t: translatedTextVerifyIdentity } = useTranslation('otp', {
    keyPrefix: 'verifyIdentity',
  });

  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const { resendMagicLink } = useResendMagicLink();

  const { challengeId, emailAddress, identityId } =
    useLocalSearchParams<VerifyIdentitySearchParams>();

  const goBack = () => {
    void handleLogout({ shouldNavigateToLogin: true });
  };

  useBackHandler(goBack);

  useViewDidFocusScreenAnalyticsEvent({
    key: '/login/verify-identity',
    customAttributes: {
      verification_type: 'email',
    },
  });

  const resendMagicLinkAnalyticsEvent = usePressableAnalytics({
    analyticsId: 'resendMagicLink',
    customAttributes: {
      verification_type: 'email',
    },
  });

  const handleResendMagicLink = async () => {
    resendMagicLinkAnalyticsEvent();
    if (challengeId) {
      await resendMagicLink({
        challengeId: challengeId,
        identityId: identityId,
      });
    }

    return {};
  };

  return challengeId && identityId ? (
    <EmailVerificationScreen
      titleText={translatedTextVerifyIdentity('title')}
      headerText={translatedTextVerifyIdentity('header')}
      description={translatedTextVerifyIdentity('description', {
        emailId: emailAddress ?? 'your email',
      })}
      openMailButtonLabel={translatedTextVerifyIdentity('buttons.openEmail')}
      newLinkSentText={translatedTextVerifyIdentity('newLinkSent')}
      genericErrorText={translatedTextOtp('genericError')}
      onBack={goBack}
      onResendLink={handleResendMagicLink}
      flowKey={OtpFlowKey.LOGIN}
    />
  ) : (
    <VerifyIdentityErrorScreen />
  );
};

export default VerifyIdentity;
